import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { collection, collectionGroup, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import styles from '../ManagerDashboard/Profile.module.css';  // Using the existing styles

const BandShowsModal = ({ isOpen, onClose, bandId }) => {
  const [displayedShows, setDisplayedShows] = useState(15);
  const [shows, setShows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && bandId) {
      fetchBandShows();
    }
  }, [isOpen, bandId]);

  const fetchBandShows = async () => {
    if (!bandId) return;
    
    setIsLoading(true);
    try {
      const db = getFirestoreInstance();
      
      // Query all verified tour dates across all tours for this band
      const tourDatesQuery = query(
        collectionGroup(db, 'tourDates'),
        where('verified', '==', true)
      );
      
      const tourDatesSnapshot = await getDocs(tourDatesQuery);
      
      // Filter to get only dates belonging to this band
      const bandShows = [];
      
      for (const docSnapshot of tourDatesSnapshot.docs) {
        // Extract band ID from the document path
        const pathSegments = docSnapshot.ref.path.split('/');
        
        // Check if this is a band_tours document
        if (pathSegments.length < 2 || 
            pathSegments[0] !== 'band_tours' || 
            pathSegments[1] !== bandId) {
          continue;
        }
        
        const tourId = pathSegments[3]; // band_tours/{bandId}/tours/{tourId}/...
        
        // Get the tour details
        const tourDoc = await getDoc(doc(db, 'band_tours', bandId, 'tours', tourId));
        
        if (tourDoc.exists()) {
          const tourData = tourDoc.data();
          const dateData = docSnapshot.data();
          
          bandShows.push({
            id: docSnapshot.id,
            tourId,
            tourName: tourData.tourName,
            actName: tourData.actName,
            date: dateData.date,
            venueDetails: dateData.venueDetails
          });
        }
      }
      
      // Sort by date (newest first)
      bandShows.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setShows(bandShows);
    } catch (error) {
      console.error('Error fetching band shows:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      weekday: 'short'
    });
  };

  if (!isOpen) return null;

  return (
    <div className={styles['modal-overlay']}>
      <div className={styles['modal-content']}>
        <button className={styles['modal-close']} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2>Verified Shows</h2>
        
        {isLoading ? (
          <div className={styles['loading-indicator']}>
            Loading show history...
          </div>
        ) : shows.length > 0 ? (
          <div className={styles['shows-list']}>
            {shows.slice(0, displayedShows).map((show) => (
              <div key={`${show.tourId}-${show.id}`} className={styles['show-card']}>
                <div className={styles['show-date']}>{formatDate(show.date)}</div>
                <div className={styles['show-details']}>
                  <h3>{show.tourName}</h3>
                  <p className={styles['act-name']}>{show.actName}</p>
                  <p className={styles['venue-name']}>
                    {show.venueDetails?.displayName || 'Venue not specified'}
                  </p>
                  <p className={styles['venue-address']}>
                    {show.venueDetails?.formattedAddress || 'Address not specified'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles['no-data-message']}>
            No verified shows available
          </div>
        )}
        
        {displayedShows < shows.length && (
          <button 
            className={styles['load-more-button']}
            onClick={() => setDisplayedShows(prev => prev + 15)}
          >
            Show More
          </button>
        )}
      </div>
    </div>
  );
};

export default BandShowsModal;